import { Suspense, lazy, useEffect } from "react";
import { Route, Routes, useLocation } from "react-router";
import { AuthProvider, useAuth } from "./auth/auth-provider";
import { RequireAuth } from "./auth/require-auth";
import AppLayout from "./layouts/app-layout";
import AuthLayout from "./layouts/auth-layout";
import LegalLayout from "./layouts/legal-layout";
import LandingPage from "./pages/landing";
import LoginPage from "./pages/login";
import SignUpPage from "./pages/sign-up";
import ForgotPasswordPage from "./pages/forgot-password";
import UpdatePasswordPage from "./pages/update-password";
import AuthConfirmPage from "./pages/auth-confirm";
import TermsPage from "./pages/terms";
import PrivacyPage from "./pages/privacy";
import ScriptureCopyrightPage from "./pages/scripture-copyright";
import NotFoundPage from "./pages/not-found";
import { usePageMeta } from "./use-page-meta";

// 로그인 후 화면은 별도 청크로 분리 — 랜딩·약관 같은 공개 페이지(프리렌더 대상)는 동기 import 유지.
const HomePage = lazy(() => import("./pages/home"));
const NoteDetailPage = lazy(() => import("./pages/note-detail"));
const ActionsPage = lazy(() => import("./pages/actions"));
const SettingsPage = lazy(() => import("./pages/settings"));

/** 화면 이동 후 맨 위로(또는 URL의 #해시 위치로) 스크롤 — 라우터는 기본으로 안 해준다(예: /privacy#3). */
function ScrollManager() {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    const target = hash
      ? document.getElementById(decodeURIComponent(hash.slice(1)))
      : null;
    if (target) target.scrollIntoView();
    else window.scrollTo(0, 0);
  }, [pathname, hash]);
  return null;
}

/**
 * "/"는 두 얼굴이다: 로그인했으면 홈(노트 목록), 아니면 랜딩. 랜딩은 빌드 때 정적 HTML로도 만들어서
 * (scripts/prerender.ts) 검색엔진·공유 미리보기가 JS 없이도 읽는다. 세션을 읽는 동안(loading)엔
 * 랜딩을 그려 둔다 — 로그인 사용자에겐 index.html의 has-session 표시가 그 순간을 가려준다.
 */
function RootPage() {
  const { user, loading } = useAuth();
  usePageMeta("/");

  if (!loading && user) {
    return (
      <AppLayout>
        <HomePage />
      </AppLayout>
    );
  }
  return <LandingPage />;
}

/** 세션 확인이 끝나면 index.html이 걸어둔 has-session(첫 화면 숨김)을 푼다. */
function RevealWhenReady() {
  const { loading } = useAuth();
  useEffect(() => {
    if (!loading) document.documentElement.classList.remove("has-session");
  }, [loading]);
  return null;
}

export function App() {
  return (
    <AuthProvider>
      <ScrollManager />
      <RevealWhenReady />
      <Suspense fallback={null}>
        <Routes>
          <Route path="/" element={<RootPage />} />

          <Route element={<RequireAuth />}>
            <Route element={<AppLayout />}>
              <Route path="/notes/:id" element={<NoteDetailPage />} />
              <Route path="/actions" element={<ActionsPage />} />
              <Route path="/settings" element={<SettingsPage />} />
            </Route>
          </Route>

          <Route path="/auth" element={<AuthLayout />}>
            <Route path="login" element={<LoginPage />} />
            <Route path="sign-up" element={<SignUpPage />} />
            <Route path="forgot-password" element={<ForgotPasswordPage />} />
            <Route path="update-password" element={<UpdatePasswordPage />} />
            <Route path="confirm" element={<AuthConfirmPage />} />
          </Route>

          <Route element={<LegalLayout />}>
            <Route path="/terms" element={<TermsPage />} />
            <Route path="/privacy" element={<PrivacyPage />} />
            <Route
              path="/scripture-copyright"
              element={<ScriptureCopyrightPage />}
            />
          </Route>

          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </Suspense>
    </AuthProvider>
  );
}
